export type Currency = "USD" | "DOP";
export type ProductCondition = "nuevo" | "usado";

export type AnnouncementStatus =
  | "draft"
  | "pending_completion"
  | "pending_moderation"
  | "active"
  | "paused"
  | "sold"
  | "rejected"
  | "archived";

export interface SellerProduct {
  id: number;
  seller_id: string;
  title: string;
  description: string | null;
  price: number;
  currency: string;
  category: string | null;
  category_id: number | null;
  condition: string | null;
  brand: string | null;
  model: string | null;
  negotiable: boolean;
  min_price: number | null;
  quantity: number;
  province: string | null;
  municipality: string | null;
  sector: string | null;
  images: string[];
  video_url: string | null;
  ticket_number: string | null;
  status: string;
  views: number;
  featured: boolean;
  published_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export type BadgeTone =
  | "amber"
  | "green"
  | "indigo"
  | "neutral"
  | "red"
  | "teal";

export interface AnnouncementStatusMeta {
  label: string;
  tone: BadgeTone;
  icon: string;
  description: string;
}

/** Metadatos de visualización para cada estado de anuncio. */
export const ANNOUNCEMENT_STATUS_META: Record<
  string,
  AnnouncementStatusMeta
> = {
  draft: {
    label: "Borrador",
    tone: "neutral",
    icon: "ri-draft-line",
    description: "Guardado como borrador",
  },
  pending_completion: {
    label: "Pendiente de completar",
    tone: "amber",
    icon: "ri-alert-line",
    description: "Falta información obligatoria",
  },
  pending_moderation: {
    label: "Pendiente de revisión",
    tone: "amber",
    icon: "ri-time-line",
    description: "En revisión por un moderador",
  },
  active: {
    label: "Disponible",
    tone: "green",
    icon: "ri-checkbox-circle-line",
    description: "Publicado y visible",
  },
  paused: {
    label: "Pausado",
    tone: "neutral",
    icon: "ri-pause-circle-line",
    description: "Oculto temporalmente",
  },
  sold: {
    label: "Vendido",
    tone: "indigo",
    icon: "ri-hand-coin-line",
    description: "Marcado como vendido",
  },
  rejected: {
    label: "Rechazado",
    tone: "red",
    icon: "ri-close-circle-line",
    description: "Necesita cambios",
  },
  archived: {
    label: "Archivado",
    tone: "neutral",
    icon: "ri-archive-line",
    description: "Archivado",
  },
};

/** Etiquetas de la oferta. */
export interface Offer {
  id: number;
  seller_id: string;
  name: string;
  description: string | null;
  conditions: string | null;
  cover_image: string | null;
  normal_price: number | null;
  offer_price: number | null;
  discount_percent: number | null;
  starts_at: string | null;
  ends_at: string | null;
  quantity: number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const OFFER_STATUS_META: Record<string, AnnouncementStatusMeta> = {
  draft: {
    label: "Borrador",
    tone: "neutral",
    icon: "ri-draft-line",
    description: "Aún no publicada",
  },
  scheduled: {
    label: "Programada",
    tone: "teal",
    icon: "ri-calendar-line",
    description: "Se activará en la fecha indicada",
  },
  active: {
    label: "Activa",
    tone: "green",
    icon: "ri-flashlight-line",
    description: "Visible en el marketplace",
  },
  finished: {
    label: "Finalizada",
    tone: "indigo",
    icon: "ri-flag-line",
    description: "Ya terminó",
  },
  paused: {
    label: "Pausada",
    tone: "neutral",
    icon: "ri-pause-circle-line",
    description: "Temporalmente oculta",
  },
  cancelled: {
    label: "Cancelada",
    tone: "red",
    icon: "ri-close-circle-line",
    description: "Cancelada",
  },
};

export function isFullUrl(src: string): boolean {
  return src.startsWith("http://") || src.startsWith("https://");
}

export function formatPrice(value: number, currency?: string): string {
  const formatted = Number(value).toLocaleString("es-DO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return currency === "USD" ? `US$${formatted}` : `RD$${formatted}`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} día${days === 1 ? "" : "s"}`;
  return formatDate(iso);
}

/** Calcula los campos obligatorios que faltan en un anuncio. */
export function missingAnnouncementFields(
  p: Partial<SellerProduct>,
  isAdmin = false
): string[] {
  const missing: string[] = [];
  if (!p.title || p.title.trim().length < 3) missing.push("Título");
  if (!p.category && !p.category_id) missing.push("Categoría");
  if (!p.description || p.description.trim().length < 3)
    missing.push("Descripción");
  if (!p.price || Number(p.price) <= 0) missing.push("Precio");
  if (!p.images || p.images.length === 0) missing.push("Fotografía");
  else if (!isAdmin && p.images.length < 2)
    missing.push("Fotografía de verificación");
  if (!p.province) missing.push("Ubicación");
  return missing;
}